import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AlertDto,
  JsonPatchOperationModel,
  TrackingSettingsResponseDto,
  VehicleDto,
  VehicleMetricsResponseDto,
} from '@fleet-beat/api';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  GetManyDto,
  GetOneDto,
  PageableListDto,
  PageableListV2Dto,
  ResponseDto,
} from '@fleet-beat/core';
import { Observable, catchError, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AlertsApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/alerts`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(data: GetManyDto, auth: AuthHeaders): Observable<PageableListDto<VehicleDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params: data }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<PageableListDto<VehicleDto>>) => this.baseApiService.mapResponse(response)),
    );
  }

  getManyV2(data: GetManyDto, auth: AuthHeaders): Observable<PageableListV2Dto<AlertDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params: data }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<AlertDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<AlertDto[]>) => {
        return {
          data: response.data.map<AlertDto>((alert: AlertDto) => {
            return {
              ...alert,
              userName: alert.user.fullName,
              driverName: alert.driver.fullName,
            };
          }),
          ...response.meta,
        };
      }),
    );
  }

  getOne(id: string, auth: AuthHeaders, params?: GetOneDto): Observable<VehicleDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getParameters(id: string, auth: AuthHeaders, params?: GetOneDto): Observable<VehicleMetricsResponseDto[]> {
    return this.baseApiService.get(`${this.baseUrl}/${id}/parameters`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleMetricsResponseDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  delete(id: string, auth: AuthHeaders): Observable<VehicleDto> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  addTruck(data: VehicleDto, auth: AuthHeaders): Observable<VehicleDto> {
    return this.baseApiService.post(this.baseUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  addAlert(data: AlertDto, auth: AuthHeaders): Observable<AlertDto> {
    return this.baseApiService.post(this.baseUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<AlertDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  updateTruck(auth: AuthHeaders, payload: JsonPatchOperationModel[], id: string): Observable<VehicleDto> {
    return this.baseApiService.put(`${this.baseUrl}/${id}`, payload, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  updateTrackingSettings(id: string, data: VehicleDto, auth: AuthHeaders): Observable<TrackingSettingsResponseDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}/tracking-settings`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<TrackingSettingsResponseDto>) => this.baseApiService.mapResponse(response)),
    );
  }
}
